import type { JobOffer } from '../types/jobOffer';
import type { Beneficiaire } from '../types/beneficiaire';

type Candidate = JobOffer['candidates'][number];

function escapeHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function resultLabel(status: Candidate['status']): string {
  return status === 'placed' ? 'Placé' : 'Proposé';
}

function cvSentLabel(beneficiaire: Beneficiaire): string {
  return beneficiaire.cvOk ? 'Oui' : 'Non';
}

function oveLabel(offer: JobOffer): string {
  return offer.transmissionStatus === 'ove' ? 'Oui' : 'Non';
}

let cachedBanner: string | null = null;

async function getBannerDataUri(): Promise<string> {
  if (cachedBanner) return cachedBanner;

  const response = await fetch('/Banniere_ANTHEA_2026.png');
  if (!response.ok) {
    throw new Error('Bannière introuvable');
  }

  const source = await createImageBitmap(await response.blob());
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Impossible de préparer la bannière');

  context.drawImage(source, 0, 0);
  const pixels = context.getImageData(0, 0, source.width, source.height).data;
  let bottom = 0;
  for (let y = source.height - 1; y >= 0; y--) {
    for (let x = 0; x < source.width; x++) {
      if (pixels[(y * source.width + x) * 4 + 3] > 0) {
        bottom = y + 1;
        break;
      }
    }
    if (bottom) break;
  }

  const cropped = document.createElement('canvas');
  cropped.width = source.width;
  cropped.height = bottom || source.height;
  cropped.getContext('2d')?.drawImage(canvas, 0, 0);
  cachedBanner = cropped.toDataURL('image/png');
  source.close();
  return cachedBanner;
}

function downloadHtmlDocument(html: string, filename: string): void {
  const blob = new Blob([html], { type: 'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SHARED_STYLES = `
  @page { size: A4 portrait; margin: 0; }
  body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #4b5563; font-size: 10pt; }
  .banner { width: 100%; height: auto; display: block; }
  .title { background: #f9df82; color: #6b7280; text-align: center; font-weight: bold; font-size: 16pt; letter-spacing: 1px; padding: 12px 8px; }
  .meta { background: #f9df82; color: #6b7280; text-align: center; font-weight: bold; font-size: 13pt; letter-spacing: 1px; padding: 10px 8px; }
  table { border-collapse: collapse; width: 100%; table-layout: fixed; }
  th { background: #fff4ca; color: #6b7280; border: 1px solid #aeb4bc; font-size: 11pt; font-weight: bold; padding: 10px 6px; height: 42px; vertical-align: middle; }
  td { border: 1px solid #c7cbd1; color: #374151; padding: 10px 7px; height: 34px; vertical-align: middle; word-wrap: break-word; }
  tbody tr:nth-child(even) td { background: #fafafa; }
  .center { text-align: center; }
  .empty { text-align: center; color: #6b7280; font-style: italic; height: 70px; }
  .footer { color: #9ca3af; font-size: 8pt; padding-top: 10px; text-align: right; }
`;

const TABLE_COLGROUP = `
      <col style="width: 17%"><col style="width: 17%"><col style="width: 12%"><col style="width: 10%">
      <col style="width: 11%"><col style="width: 12%"><col style="width: 13%"><col style="width: 8%">
`;

const TABLE_HEADERS = `
      <tr>
        <th>Entreprise</th>
        <th>Poste</th>
        <th>Contrat</th>
        <th>CV<br>envoyé</th>
        <th>Entretien</th>
        <th>Résultat</th>
        <th>Notes</th>
        <th>OVE</th>
      </tr>
`;

function bannerRow(bannerDataUri: string): string {
  return `    <tr><td colspan="8" style="padding: 0; border: none;"><img class="banner" width="100%" src="${bannerDataUri}" alt="Anthéa" /></td></tr>`;
}

export async function generateCandidatePropositionDocument(
  beneficiaire: Beneficiaire,
  offers: JobOffer[],
): Promise<void> {
  const bannerDataUri = await getBannerDataUri();
  const candidateOffers = offers.filter(offer =>
    offer.candidates?.some(c => c.beneficiaireId === beneficiaire.id)
  );

  const rows = candidateOffers.length
    ? candidateOffers.map(offer => {
        const candidate = offer.candidates.find(c => c.beneficiaireId === beneficiaire.id);
        const cvSent = beneficiaire.cvOk ? 'Oui' : 'Non';
        const result = candidate?.status === 'placed' ? 'Placé' : 'Proposé';
        const ove = offer.transmissionStatus === 'ove' ? 'Oui' : 'Non';

        return `<tr>
    <td>${escapeHtml(offer.company)}</td>
    <td>${escapeHtml(offer.title)}</td>
    <td>${escapeHtml(offer.type)}</td>
    <td class="center">${escapeHtml(cvSent)}</td>
    <td></td>
    <td class="center">${escapeHtml(result)}</td>
    <td></td>
    <td class="center">${escapeHtml(ove)}</td>
  </tr>`;
      }).join('')
    : `<tr><td colspan="8" class="empty">Aucun poste proposé</td></tr>`;

  const html = `<!DOCTYPE html>
<html lang="fr" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
<meta charset="UTF-8">
<title>Postes proposés - ${escapeHtml(beneficiaire.firstName)} ${escapeHtml(beneficiaire.lastName)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
<style>${SHARED_STYLES}</style>
</head>
<body>
  <table>
    ${bannerRow(bannerDataUri)}
    <tr><td class="title" colspan="8">POSTES PROPOSÉS</td></tr>
    <tr><td class="meta" colspan="8">Nom du bénéficiaire : ${escapeHtml(beneficiaire.firstName)} ${escapeHtml(beneficiaire.lastName)}</td></tr>
  </table>
  <table>
    <colgroup>${TABLE_COLGROUP}</colgroup>
    <thead>${TABLE_HEADERS}</thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">
    NOM : ${escapeHtml(beneficiaire.lastName)} — PRENOM : ${escapeHtml(beneficiaire.firstName)}<br>
    ANTHEA — Document de suivi des postes proposés
  </div>
</body>
</html>`;

  downloadHtmlDocument(html, `postes-proposes-${beneficiaire.firstName}-${beneficiaire.lastName}.doc`);
}

function candidateRow(
  offer: JobOffer,
  candidate: Candidate,
  beneficiaire: Beneficiaire | undefined,
): string {
  const candidateTitle = beneficiaire?.title || '';
  const cvSent = beneficiaire ? cvSentLabel(beneficiaire) : '';

  return `<tr>
    <td>${escapeHtml(offer.company)}</td>
    <td>${escapeHtml(offer.title || candidateTitle)}</td>
    <td>${escapeHtml(offer.type)}</td>
    <td class="center">${escapeHtml(cvSent)}</td>
    <td></td>
    <td class="center">${escapeHtml(resultLabel(candidate.status))}</td>
    <td></td>
    <td class="center">${escapeHtml(oveLabel(offer))}</td>
  </tr>`;
}

export async function generatePropositionDocument(
  offer: JobOffer,
  beneficiaires: Beneficiaire[],
): Promise<void> {
  const bannerDataUri = await getBannerDataUri();
  const candidates = offer.candidates || [];
  const rows = candidates.length
    ? candidates.map(candidate => {
        const beneficiaire = beneficiaires.find(item => item.id === candidate.beneficiaireId);
        return candidateRow(offer, candidate, beneficiaire);
      }).join('')
    : `<tr><td colspan="8" class="empty">Aucun poste proposé</td></tr>`;

  const html = `<!DOCTYPE html>
<html lang="fr" xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word">
<head>
<meta charset="UTF-8">
<title>Postes proposés - ${escapeHtml(offer.company)}</title>
<!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View></w:WordDocument></xml><![endif]-->
<style>
${SHARED_STYLES}
</style>
</head>
<body>
  <table>
    ${bannerRow(bannerDataUri)}
    <tr><td class="title" colspan="8">POSTES PROPOSÉS</td></tr>
    <tr><td class="meta" colspan="8">Nom de la convention : ${escapeHtml(offer.company)}${offer.location ? ` — ${escapeHtml(offer.location)}` : ''}</td></tr>
  </table>
  <table>
    <colgroup>${TABLE_COLGROUP}</colgroup>
    <thead>${TABLE_HEADERS}</thead>
    <tbody>${rows}</tbody>
  </table>
  <div class="footer">ANTHEA — Document de suivi des postes proposés</div>
</body>
</html>`;

  downloadHtmlDocument(html, `postes-proposes-${offer.company || 'anthea'}.doc`);
}
